import { ButtonTypeEnum, PaymentOptions, SnackBarVariants } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import { defaultCountry } from '@app/constants';
import * as constants from '@app/constants/index';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { MetaDataService } from '@app/services/meta-data.service';
import { SharedService } from '@app/services/shared.service';
import {
	Button,
	CardContent,
	FormControl,
	FormHelperText,
	Grid,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Select,
	TextField,
	Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Axios from 'axios';
import dayjs from 'dayjs';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const metaDataService: MetaDataService = Injector.get(MetaDataService);
const sharedService: SharedService = Injector.get(SharedService);

class PaymentMethodComponent extends Component<
	models.IPaymentMethodComponentProps,
	models.IPaymentMethodComponentState
> {
	public static getDerivedStateFromProps(
		props: models.IPaymentMethodComponentProps,
		state: models.IPaymentMethodComponentState
	): models.IPaymentMethodComponentState {
		const finalState = { ...state };

		if (props.paymentMethodData?.isUpdate || props.isForQuickSignUp) {
			finalState.addMethod = true;
		}

		if (!props.paymentMethodData?.isUpdate && !props.isForQuickSignUp) {
			finalState.addMethod = false;
		}

		if (props.paymentMethodData?.defaultMethod) {
			finalState.defaultPayment = props.paymentMethodData.defaultMethod;
		}

		return finalState;
	}

	private formRef: React.RefObject<Formik>;
	private PaymentMethodSchema: any;
	private paymentDetailOptions: models.IPaymentOptions;
	private paymentDetailsAuxiliary: models.IPaymentValueSetUp;

	constructor(props: models.IPaymentMethodComponentProps) {
		super(props);
		this.state = {
			loading: false,
			paymentOption: props.paymentMethodData?.defaultMethod ?? PaymentOptions.None,
			defaultPayment: props.paymentMethodData?.defaultMethod ?? PaymentOptions.None,
			billingCountry: defaultCountry,
			countries: [],
			states: [],
			addMethod: false,
		};
		this.formRef = React.createRef();
		this.initPaymentForm();
	}

	public componentDidMount() {
		this.updatePaymentSchemaAndState(PaymentOptions.None);
		this.getAllCountries();
		// Get States for US
		this.getStatesForCountry();
		if (this.props.paymentMethodData.prefilledData) {
			this.paymentDetailsAuxiliary = { ...this.props.paymentMethodData.prefilledData };
			this.settingForm();
		}

		if (this.props.paymentMethodData?.prefilledPaymentMethod) {
			this.updatePaymentSchemaAndState(this.props.paymentMethodData?.prefilledPaymentMethod);
		}
	}

	public componentDidUpdate(
		prevProps: models.IPaymentMethodComponentProps,
		prevState: models.IPaymentMethodComponentState
	) {
		if (prevState.paymentOption !== this.state.paymentOption) {
			this.updatePaymentSchemaAndState(this.state.paymentOption);
		}

		if (prevProps.paymentMethodData?.prefilledData !== this.props.paymentMethodData?.prefilledData) {
			this.paymentDetailsAuxiliary = { ...this.props.paymentMethodData.prefilledData };
			this.settingForm();
		}

		if (prevState.defaultPayment !== this.state.defaultPayment) {
			this.setState((state: models.IPaymentMethodComponentState) => ({
				paymentOption: state.defaultPayment,
			}));
		}
	}

	public updatePaymentSchemaAndState(value: string) {
		switch (value) {
			case PaymentOptions.CreditCard:
				this.PaymentMethodSchema = Yup.object().shape<models.IPaymentOptions>({
					name: Yup.string().required(intlService.GetFormattedMessage('app.billing.payment-name-required-validation')),
					cardNumber: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-card-number-required-validation'))
						.min(13, intlService.GetFormattedMessage('app.billing.payment-card-number-length-validation'))
						.max(16, intlService.GetFormattedMessage('app.billing.payment-card-number-length-validation')),
					country: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-country-required-validation')
					),
					address1: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-address1-required-validation')
					),
					city: Yup.string().required(intlService.GetFormattedMessage('app.billing.payment-city-required-validation')),
					state: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-state-required-validation')
					),
					postalCode: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-postal-code-required-validation'))
						.matches(/\d+/, intlService.GetFormattedMessage('app.billing.payment-postal-code-numeric-validation')),
					expirationDate: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-expiry-date-required-validation')
					),
					securityCode: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-security-code-required-validation'))
						.matches(/\d+/, intlService.GetFormattedMessage('app.billing.payment-security-code-numeric-validation'))
						.min(3, intlService.GetFormattedMessage('app.billing.payment-security-code-min-length-validation'))
						.max(4, intlService.GetFormattedMessage('app.billing.payment-security-code-max-length-validation')),
					accountNumber: Yup.string().notRequired(),
					routingNumber: Yup.string().notRequired(),
					address2: Yup.string().notRequired(),
					phoneNumber: Yup.string().notRequired(),
				});
				this.setState({
					paymentOption: PaymentOptions.CreditCard,
				});
				break;
			case PaymentOptions.ACH:
				this.PaymentMethodSchema = Yup.object().shape<models.IPaymentOptions>({
					name: Yup.string().required(intlService.GetFormattedMessage('app.billing.payment-name-required-validation')),
					accountNumber: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-account-number-required-validation'))
						.matches(/\d+/, intlService.GetFormattedMessage('app.billing.payment-account-number-numeric-validation'))
						.min(8, intlService.GetFormattedMessage('app.billing.payment-account-number-min-length-validation'))
						.max(16, intlService.GetFormattedMessage('app.billing.payment-account-number-max-length-validation')),
					routingNumber: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-routing-number-required-validation'))
						.length(9, intlService.GetFormattedMessage('app.billing.payment-routing-number-length-validation')),
					country: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-country-required-validation')
					),
					address1: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-address1-required-validation')
					),
					city: Yup.string().required(intlService.GetFormattedMessage('app.billing.payment-city-required-validation')),
					state: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-state-required-validation')
					),
					postalCode: Yup.string()
						.required(intlService.GetFormattedMessage('app.billing.payment-postal-code-required-validation'))
						.matches(/\d+/, intlService.GetFormattedMessage('app.billing.payment-postal-code-numeric-validation')),
					phoneNumber: Yup.string().required(
						intlService.GetFormattedMessage('app.billing.payment-phone-required-validation')
					),
					expirationDate: Yup.string().notRequired(),
					securityCode: Yup.string().notRequired(),
					address2: Yup.string().notRequired(),
					cardNumber: Yup.string().notRequired(),
				});
				this.setState({ paymentOption: PaymentOptions.ACH });
				break;
			default:
				this.PaymentMethodSchema = Yup.object().shape<models.IPaymentOptions>({
					name: Yup.string().notRequired(),
					accountNumber: Yup.string().notRequired(),
					routingNumber: Yup.string().notRequired(),
					country: Yup.string().notRequired(),
					address1: Yup.string().notRequired(),
					city: Yup.string().notRequired(),
					state: Yup.string().notRequired(),
					postalCode: Yup.string().notRequired(),
					expirationDate: Yup.string().notRequired(),
					securityCode: Yup.string().notRequired(),
					address2: Yup.string().notRequired(),
					cardNumber: Yup.string().notRequired(),
					phoneNumber: Yup.string().notRequired(),
				});
				this.setState({ paymentOption: PaymentOptions.None });
				break;
		}
	}

	public render() {
		return (
			<Grid container={true} direction='column' spacing={2}>
				<Grid item={true} lg={12}>
					<Grid container={true} direction='row' alignItems='center'>
						{
							// Condition displays payment selection field only if user clicked on UPDATE button
							this.state.addMethod ||
							(this.props.hideNoPaymentScreen && this.state.defaultPayment === PaymentOptions.None) ? (
								<>
									<Grid item={true} xs={12} md={4} lg={4} xl={3}>
										<Typography variant='subtitle2' color='inherit'>
											{this.props.intl.formatMessage({
												id: 'app.billing.payment-method-select-label',
											})}
										</Typography>
									</Grid>
									<Grid item={true} xs={12} md={8} lg={8} xl={9}>
										<Select
											fullWidth={true}
											input={<OutlinedInput labelWidth={0} />}
											value={this.state.paymentOption}
											onChange={(e: any) => this.updatePaymentSchemaAndState(e.target.value)}
											renderValue={(selected) => (selected as string[]).toString()}
										>
											<MenuItem key={PaymentOptions.None} value={PaymentOptions.None}>
												<ListItemText primary={PaymentOptions.None} />
											</MenuItem>
											<MenuItem key={PaymentOptions.CreditCard} value={PaymentOptions.CreditCard}>
												<ListItemText primary={PaymentOptions.CreditCard} />
											</MenuItem>
											<MenuItem key={PaymentOptions.ACH} value={PaymentOptions.ACH}>
												<ListItemText primary={PaymentOptions.ACH} />
											</MenuItem>
										</Select>
									</Grid>
								</>
							) : null
						}
						<Formik
							ref={this.formRef}
							initialValues={this.paymentDetailOptions}
							onSubmit={() => {}}
							validationSchema={this.PaymentMethodSchema}
							enableReinitialize={true}
							render={(props: FormikProps<FormikValues>) => (
								<Form noValidate={true} className='width-100'>
									{
										// This condition hides the readonly form or editable form
										// if payment method is default or selected method is default
										this.state.paymentOption !== PaymentOptions.None ? (
											<>
												<fieldset className='mb-2 mt-1'>
													<legend>
														<Typography variant='h6' component='h6'>
															{this.props.intl.formatMessage({
																id: 'app.billing.payment-payment-information-label',
															})}
														</Typography>
													</legend>
													<Field
														label={this.props.intl.formatMessage({
															id: 'app.billing.payment-method-name-label',
														})}
														disabled={!this.state.addMethod}
														margin='normal'
														variant='outlined'
														component={TextField}
														type='text'
														value={props.values.name}
														required={true}
														fullWidth={true}
														onChange={(e: React.ChangeEvent) => props.handleChange('name')(e)}
														error={!!((props.values.name.length || props.submitCount) && !!props.errors.name)}
														helperText={
															props.values.name.length || props.submitCount
																? props.errors.name
																: this.state.paymentOption === PaymentOptions.CreditCard
																? this.props.intl.formatMessage({
																		id: 'app.billing.payment-card-number-info',
																  })
																: null
														}
													/>
													{
														// Replaces some credit card form fields with ACH form field
														// in case of condition failure
														this.state.paymentOption === PaymentOptions.CreditCard ? (
															<>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-card-number-label',
																	})}
																	disabled={!this.state.addMethod}
																	margin='normal'
																	variant='outlined'
																	component={TextField}
																	type='tel'
																	value={props.values.cardNumber}
																	required={true}
																	fullWidth={true}
																	onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
																		this.setNumOnly('cardNumber', e.target.value, props.setFieldValue)
																	}
																	error={
																		!!(
																			(props.values.cardNumber.length || props.submitCount) &&
																			!!props.errors.cardNumber
																		)
																	}
																	helperText={
																		props.values.cardNumber.length || props.submitCount ? props.errors.cardNumber : null
																	}
																/>
																<Field
																	margin='normal'
																	variant='outlined'
																	type='text'
																	required={true}
																	fullWidth={true}
																	render={(fields: any) => (
																		<CustomDatePicker
																			margin='normal'
																			disablePast={true}
																			disabled={!this.state.addMethod}
																			format={constants.datePickerShortFormat}
																			label={this.props.intl.formatMessage({
																				id: 'app.billing.payment-method-expiry-date-label',
																			})}
																			value={props.values.expirationDate}
																			views={['month', 'year']}
																			onChange={(date: dayjs.Dayjs) =>
																				props.handleChange('expirationDate')(date.toDate())
																			}
																		/>
																	)}
																	error={
																		!!(
																			(props.values.expirationDate.length || props.submitCount) &&
																			!!props.errors.expirationDate
																		)
																	}
																	helperText={
																		props.values.expirationDate.length || props.submitCount
																			? props.errors.expirationDate
																			: null
																	}
																/>
																{
																	// Displays security code field only if form is editable and
																	// payment method is Credit Card
																	this.state.addMethod ? (
																		<Field
																			label={this.props.intl.formatMessage({
																				id: 'app.billing.payment-method-security-code-label',
																			})}
																			margin='normal'
																			variant='outlined'
																			component={TextField}
																			type='text'
																			required={true}
																			value={props.values.securityCode}
																			fullWidth={true}
																			onChange={(e: React.ChangeEvent) => props.handleChange('securityCode')(e)}
																			error={
																				!!(
																					(props.values.securityCode.length || props.submitCount) &&
																					!!props.errors.securityCode
																				)
																			}
																			helperText={
																				props.values.securityCode.length || props.submitCount
																					? props.errors.securityCode
																					: null
																			}
																		/>
																	) : null
																}
															</>
														) : (
															<>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-routing-number-label',
																	})}
																	disabled={!this.state.addMethod}
																	margin='normal'
																	variant='outlined'
																	component={TextField}
																	type='text'
																	value={props.values.routingNumber}
																	required={true}
																	fullWidth={true}
																	onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
																		this.setNumOnly('routingNumber', e.target.value, props.setFieldValue)
																	}
																	error={
																		!!(
																			(props.values.routingNumber.length || props.submitCount) &&
																			!!props.errors.routingNumber
																		)
																	}
																	helperText={
																		props.values.routingNumber.length || props.submitCount
																			? props.errors.routingNumber
																			: null
																	}
																/>

																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-account-number-label',
																	})}
																	disabled={!this.state.addMethod}
																	margin='normal'
																	variant='outlined'
																	component={TextField}
																	type='text'
																	required={true}
																	fullWidth={true}
																	value={props.values.accountNumber}
																	onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
																		this.setNumOnly('accountNumber', e.target.value, props.setFieldValue)
																	}
																	error={
																		!!(
																			(props.values.accountNumber.length || props.submitCount) &&
																			!!props.errors.accountNumber
																		)
																	}
																	helperText={
																		props.values.accountNumber.length || props.submitCount
																			? props.errors.accountNumber
																			: null
																	}
																/>
															</>
														)
													}
												</fieldset>
												{
													// Checks and displays form fields only when form is not in readonly mode
													this.state.addMethod ? (
														<>
															<fieldset className='mb-2'>
																<legend>
																	<Typography variant='h6' component='h6'>
																		{this.props.intl.formatMessage({
																			id: 'app.billing.payment-method-billing-info-label',
																		})}
																	</Typography>
																</legend>
																<Field
																	margin='normal'
																	variant='outlined'
																	type='text'
																	required={true}
																	fullWidth={true}
																	value={props.values.country}
																	render={(fields: any) => (
																		<FormControl fullWidth={true} margin='normal'>
																			<Autocomplete
																				options={this.state.countries}
																				onChange={(e: React.ChangeEvent<HTMLInputElement>, value: models.ICountry) => {
																					if (value) {
																						props.handleChange('country')({ ...e, target: { value: value.code } });
																					} else {
																						props.handleChange('country')({ ...e, target: { value: '' } });
																					}

																					this.setState({
																						billingCountry: value?.code,
																					});
																					props.handleChange('state')({ ...e, target: { value: '' } });
																				}}
																				getOptionLabel={(option) => `${option.code} - ${option.name}`}
																				defaultValue={this.state.countries.find((x) => x.code === defaultCountry)}
																				renderInput={(params) => (
																					<TextField
																						{...params}
																						label={intlService.GetFormattedMessage(
																							'app.billing.payment-method-country-label'
																						)}
																						variant='outlined'
																						error={
																							!!(
																								(props.values.country.length || props.submitCount) &&
																								!!props.errors.country
																							)
																						}
																					/>
																				)}
																			/>
																			{!!(
																				(props.values.country.length || props.submitCount) &&
																				!!props.errors.country
																			) && <FormHelperText error={true}>{props.errors.country}</FormHelperText>}
																		</FormControl>
																	)}
																/>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-address1-label',
																	})}
																	margin='normal'
																	variant='outlined'
																	type='text'
																	required={true}
																	fullWidth={true}
																	value={props.values.address1}
																	component={TextField}
																	onChange={(e: React.ChangeEvent) => props.handleChange('address1')(e)}
																	error={
																		!!((props.values.address1.length || props.submitCount) && !!props.errors.address1)
																	}
																	helperText={
																		props.values.address1.length || props.submitCount ? props.errors.address1 : null
																	}
																/>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-address2-label',
																	})}
																	margin='normal'
																	variant='outlined'
																	type='text'
																	fullWidth={true}
																	value={props.values.address2}
																	component={TextField}
																	onChange={(e: React.ChangeEvent) => props.handleChange('address2')(e)}
																/>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-city-label',
																	})}
																	margin='normal'
																	variant='outlined'
																	type='text'
																	required={true}
																	fullWidth={true}
																	value={props.values.city}
																	component={TextField}
																	onChange={(e: React.ChangeEvent) => props.handleChange('city')(e)}
																	error={!!((props.values.city.length || props.submitCount) && !!props.errors.city)}
																	helperText={props.values.city.length || props.submitCount ? props.errors.city : null}
																/>
																<Field
																	margin='normal'
																	variant='outlined'
																	required={true}
																	fullWidth={true}
																	render={(fields: any) => (
																		<FormControl margin='normal' fullWidth={true}>
																			{this.renderStateDropDown(this.state.billingCountry === defaultCountry, props)}
																			{!!((props.values.state.length || props.submitCount) && !!props.errors.state) && (
																				<FormHelperText error={true}>{props.errors.state}</FormHelperText>
																			)}
																		</FormControl>
																	)}
																/>
																<Field
																	label={this.props.intl.formatMessage({
																		id: 'app.billing.payment-method-postal-label',
																	})}
																	margin='normal'
																	variant='outlined'
																	type='text'
																	required={true}
																	fullWidth={true}
																	value={props.values.postalCode}
																	component={TextField}
																	onChange={(e: React.ChangeEvent) => props.handleChange('postalCode')(e)}
																	error={
																		!!(
																			(props.values.postalCode.length || props.submitCount) &&
																			!!props.errors.postalCode
																		)
																	}
																	helperText={
																		props.values.postalCode.length || props.submitCount ? props.errors.postalCode : null
																	}
																/>
																{this.state.paymentOption === PaymentOptions.ACH && (
																	<Field
																		name='phoneNumber'
																		variant='outlined'
																		label={this.props.intl.formatMessage({
																			id: 'app.billing.payment-method-phone',
																		})}
																		component={TextField}
																		margin='normal'
																		type='text'
																		value={props.values.phoneNumber}
																		fullWidth={true}
																		required={true}
																		onChange={(e: React.ChangeEvent) => props.handleChange('phoneNumber')(e)}
																		error={
																			!!(
																				(props.values.phoneNumber.length || props.submitCount) &&
																				!!props.errors.phoneNumber
																			)
																		}
																		helperText={
																			props.values.phoneNumber.length || props.submitCount
																				? props.errors.phoneNumber
																				: null
																		}
																	/>
																)}
															</fieldset>
														</>
													) : null
												}
											</>
										) : (
											this.getNoneComponent()
										)
									}
									{!this.props.formButtons
										? // This condition hides the form SAVE or UPDATE button in case if default
										  // payment is NONE and user haven't clicked on configure button
										  (this.state.addMethod || this.state.defaultPayment !== PaymentOptions.None) && (
												<Grid container={true} justifyContent='center'>
													<Grid item={true}>
														{
															// This condition hides the form's UPDATE button
															// if pages refreshes or mounted
															!this.state.addMethod ? (
																<Button color='secondary' onClick={() => this.resetForm()} fullWidth={false}>
																	{this.props.intl.formatMessage({
																		id: 'app.billing.auto-buy-update-button-text',
																	})}
																</Button>
															) : (
																<Grid container={true} justifyContent='center'>
																	<Grid item={true}>
																		<CustomButton
																			buttonText={intlService.GetFormattedMessage(
																				'app.profile.user-info-form.save-button-text'
																			)}
																			type={ButtonTypeEnum.Button}
																			onClick={() => this.submitForm(props)()}
																			fullWidth={false}
																			disabled={this.state.loading}
																			loading={this.state.loading}
																		/>
																	</Grid>
																	<Grid item={true}>
																		<Button
																			color='primary'
																			className='ml-2'
																			onClick={() => this.settingForm()}
																			fullWidth={false}
																		>
																			{this.props.intl.formatMessage({
																				id: 'app.billing.payment-button-cancel-tag',
																			})}
																		</Button>
																	</Grid>
																</Grid>
															)
														}
													</Grid>
												</Grid>
										  )
										: this.props.formButtons(
												this.submitForm(props),
												() => this.settingForm(),
												() => this.resetForm()
										  )}
								</Form>
							)}
						/>
					</Grid>
				</Grid>
			</Grid>
		);
	}

	private submitForm = (formikProps: FormikProps<FormikValues>) => {
		return async (reqAuthHeader: string = null, callback: () => Promise<void> = null) => {
			try {
				await this.formRef.current.submitForm();

				const errors = Object.keys(this.formRef.current.state.errors).length;

				if (!errors) {
					await this.updatePaymentMethod(formikProps.values as models.IPaymentOptions, reqAuthHeader, callback);
				} else {
					throw errors;
				}
			} catch (error) {
				throw error;
			}
		};
	};

	private async getAllCountries() {
		try {
			const result = await metaDataService.getAllCountries(this.props.axiosCancellationToken);
			this.setState({ countries: result });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	}

	private async getStatesForCountry() {
		try {
			const result = await metaDataService.getStates(defaultCountry, this.props.axiosCancellationToken);
			this.setState({ states: result });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	}

	// Reset Payment Method form
	private resetForm() {
		this.initPaymentForm();
		this.setState({
			paymentOption: this.state.defaultPayment,
		});
		this.props.paymentMethodData?.onUpdateCb(true);
	}

	// Initialize Payment method form
	private initPaymentForm() {
		const date = new Date(Date.now());
		date.setMonth(date.getMonth() + 1);
		this.paymentDetailOptions = {
			name: '',
			cardNumber: '',
			expirationDate: date.toDateString(),
			securityCode: '',
			country: defaultCountry,
			address1: '',
			address2: '',
			city: '',
			state: '',
			postalCode: '',
			accountNumber: '',
			routingNumber: '',
			phoneNumber: '',
		};
	}

	// Re-setting form after clicking on CANCEL button
	private settingForm(paymentMethod?: PaymentOptions) {
		if (this.paymentDetailsAuxiliary) {
			this.paymentDetailOptions = {
				...this.paymentDetailOptions,
				name:
					this.state.paymentOption === PaymentOptions.CreditCard
						? this.paymentDetailsAuxiliary.cardHolderName
						: this.paymentDetailsAuxiliary.accountHolderName,
				accountNumber: this.accountNumberFormatter(this.paymentDetailsAuxiliary.lastFourAccount),
				cardNumber: this.cardNumberFormatter(this.paymentDetailsAuxiliary.lastFourCredit),
				routingNumber: this.paymentDetailsAuxiliary.routingNumber,
				expirationDate: this.dateCreatorFromMonthAndYear(
					this.paymentDetailsAuxiliary.expirationMonth,
					this.paymentDetailsAuxiliary.expirationYear
				),
			};
		}

		this.setState({
			paymentOption: paymentMethod ?? this.state.defaultPayment,
		});

		this.props.paymentMethodData?.onUpdateCb(false, paymentMethod);
	}

	// Remove alphabets from provided field
	private setNumOnly(
		fieldName: string,
		value: string,
		callbackFunc: (field: string, value: any, shouldValidate?: boolean) => void
	) {
		const regExMatch = value.match(/^\d+$/);
		// Trimming field value upto that index at which non-numeric value is entered and setting cardNumber field value
		callbackFunc(fieldName, regExMatch ? value : value.substr(0, value.length - 1));
	}

	private dateCreatorFromMonthAndYear(month: string, year: string): string {
		return new Date(parseInt(year, 10), parseInt(month, 10) - 1).toDateString();
	}

	private cardNumberFormatter(cardLastFourDigits: string): string {
		return this.props.intl
			.formatMessage({
				id: 'app.billing.payment-card-pattern-string',
			})
			.concat(cardLastFourDigits);
	}

	private accountNumberFormatter(accountLastFourDigits: string): string {
		return this.props.intl
			.formatMessage({
				id: 'app.billing.payment-account-number-pattern-string',
			})
			.concat(accountLastFourDigits);
	}

	private async updatePaymentMethod(
		data: models.IPaymentOptions,
		axiosReqHeader: string = null,
		callback: () => Promise<void> = null
	) {
		try {
			if (data.state.includes('-')) {
				data.state = data.state.substr(data.state.indexOf('-') + 2);
			}

			this.setState({ loading: true });
			const evaluatedData: models.IPaymentMethod = {};
			switch (this.state.paymentOption) {
				case PaymentOptions.CreditCard:
					const { routingNumber, accountNumber, ...restCredit } = {
						...data,
					};
					const dateArray = new Date(restCredit.expirationDate);
					evaluatedData.creditCardPaymentAccountResponse = {
						...restCredit,
						cardHolderName: restCredit.name,
						active: true,
						default: true,
						expirationMonth: (dateArray.getMonth() + 1).toString(),
						expirationYear: dateArray.getFullYear().toString(),
						postalCode: restCredit.postalCode,
					};
					break;
				case PaymentOptions.ACH:
					const { cardNumber, expirationDate, securityCode, ...restACH } = { ...data };
					evaluatedData.achPaymentAccountResponse = {
						...restACH,
						accountHolderName: restACH.name,
						active: true,
						default: true,
					};
					break;
				default:
					evaluatedData.creditCardPaymentAccountResponse = {
						...constants.dummyPaymentData.CreditCardPaymentAccountRequest,
					};
					evaluatedData.achPaymentAccountResponse = {
						...constants.dummyPaymentData.ACHPaymentAccountRequest,
					};
					break;
			}
			const result = await accountService.updatePaymentMethodDetails(
				evaluatedData,
				this.props.axiosCancellationToken,
				axiosReqHeader
			);

			// Calling the callback passed from parent component (if any)
			await callback?.();

			const creditCard = { ...result.creditCardPaymentAccountResponse };
			const achCheck = { ...result.achPaymentAccountResponse };
			// Payment Form data setup before formatting
			this.paymentDetailsAuxiliary = {
				cardHolderName: creditCard.cardHolderName,
				accountHolderName: achCheck.accountHolderName,
				lastFourCredit: data.cardNumber.substr(data.cardNumber.length - 4, 4),
				expirationMonth: creditCard.expirationMonth,
				expirationYear: creditCard.expirationYear,
				lastFourAccount: data.accountNumber.substr(data.accountNumber.length - 4, 4),
				routingNumber: achCheck.routingNumber,
			};
			this.setState({
				defaultPayment: this.state.paymentOption,
			});
			this.settingForm(this.state.paymentOption);

			this.props.openSnackBar(
				this.props.intl.formatMessage({
					id: 'app.snackbar.profile-payment-method-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
				throw error;
			}
		} finally {
			this.setState({
				loading: false,
			});
		}
	}

	private getNoneComponent() {
		return (
			<CardContent>
				<Grid container={true} justifyContent='center' direction='column' alignItems='center' spacing={2}>
					{!this.state.addMethod && !this.props.hideNoPaymentScreen && (
						<>
							<Grid item={true} lg={12}>
								<Typography variant='h6'>
									{this.props.intl.formatMessage({
										id: 'app.billing.payment-method-none',
									})}
								</Typography>
							</Grid>
							<Grid item={true} lg={12}>
								<Typography>
									<Button onClick={() => this.resetForm()}>
										{this.props.intl.formatMessage({
											id: 'app.billing.payment-button-configure-tag',
										})}
									</Button>
								</Typography>
							</Grid>
						</>
					)}
				</Grid>
			</CardContent>
		);
	}

	private renderStateDropDown = (isRenderDropdown: boolean, props: FormikProps<FormikValues>): JSX.Element => {
		const component = isRenderDropdown ? (
			<Autocomplete
				options={this.state.states}
				onChange={(e: React.ChangeEvent, value: models.IState) => {
					if (value) {
						props.handleChange('state')({ ...e, target: { value: value.displayName } });
					} else {
						props.handleChange('state')({ ...e, target: { value: '' } });
					}
				}}
				getOptionLabel={(option) => option.displayName}
				renderInput={(params) => (
					<TextField
						{...params}
						label={intlService.GetFormattedMessage('app.billing.payment-method-state-label')}
						error={!!((props.values.state.length || props.submitCount) && !!props.errors.state)}
						variant='outlined'
					/>
				)}
			/>
		) : (
			<TextField
				label={intlService.GetFormattedMessage('app.billing.payment-method-state-label')}
				value={props.values.state}
				onChange={(e: any) => props.handleChange('state')(e)}
				SelectProps={{
					renderValue: (selected) => (selected as string[]).toString(),
				}}
				error={!!((props.values.state.length || props.submitCount) && !!props.errors.state)}
				variant='outlined'
				required={true}
			/>
		);
		return component;
	};
}

export default withSnackBar(withAxiosCancellation(injectIntl(PaymentMethodComponent)));



// WEBPACK FOOTER //
// ./src/components/account/purchase-postage/payment-method.component.tsx